/**
 * 全局校区范围持久化（localStorage 读写，供刷新/重开后恢复顶栏选择）。
 *
 * - 仅存客户端界面偏好（'ALL' 或校区 ID），MUST NOT 视为权威事实：应用前必须经
 *   GlobalCampusContext 按权限快照校验（总部对 ACTIVE 校区集合、非总部对 grantedCampusIds），
 *   校验不通过丢弃并保持 'ALL'（防手改越权 ID 产生未授权请求）；
 * - 读写均防御性 try/catch（隐私模式/存储禁用/JSON 损坏时静默降级，不阻断应用）。
 * 对齐 admin-global-campus-switch:SC-01/SC-02/SC-03/SC-04。
 */

/** localStorage 键名（应用前缀 + 版本号，对齐 admin-web.session.v1 先例）。 */
const STORAGE_KEY = 'admin-web.global-campus.v1';

/** 全局校区范围：'ALL' 表示全部校区（页面请求不携带 campusId 参数），否则为校区 ID。 */
export type GlobalCampusScope = string;

/** 类型守卫：仅接受 'ALL' 或非空字符串校区 ID（防半写/损坏/形态非法数据进入应用）。 */
function isValidScope(v: unknown): v is GlobalCampusScope {
  return v === 'ALL' || (typeof v === 'string' && v.length > 0);
}

/** 读取持久化全局校区范围（无值/损坏/形态非法/存储禁用时返回 null，调用方回落 'ALL'）。 */
export function loadPersistedGlobalCampus(): GlobalCampusScope | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidScope(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** 持久化全局校区范围（切换器更新全局值时调用）。 */
export function persistGlobalCampus(scope: GlobalCampusScope): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // 存储禁用/超配额：仅失去刷新恢复能力，不影响当前选择
  }
}

/**
 * 清除持久化全局校区范围。
 * 登出不清除（属浏览器级界面偏好，跨用户残留由 GlobalCampusContext 权限校验兜底）；
 * 保留清除入口供测试与手动恢复使用。
 */
export function clearPersistedGlobalCampus(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // 静默降级
  }
}
